import { usePathname } from "next/navigation";
import { useProjectStore } from "@/stores/useProjectStore";
import { useConversationStore } from "@/stores/useConversationStore";
import { useEffect } from "react";
import { CNSConversation, CNSProject } from "@/models/cns";

export type BreadcrumbSection =
  | "overview"
  | "conversations"
  | "experiments"
  | "hypotheses"
  | "insights"
  | "learnings"
  | null;

export interface NavigationData {
  // Current view type
  isConversationView: boolean;
  isProjectDetailView: boolean;

  // Current entities
  currentProject: CNSProject | null;
  currentConversation: CNSConversation | null;

  // IDs
  projectId: string | null;
  conversationId: string | null;
  experimentId: string | null;
  learningId: string | null;
  insightId: string | null;
  hypothesisId: string | null;

  // Section for breadcrumb (experiments, hypotheses, insights, learnings)
  section: BreadcrumbSection;

  // Path segments
  pathSegments: string[];
}

function parseWorkbenchRoute(pathname: string): boolean {
  const pathSegments = pathname.split("/").filter(Boolean);
  return (
    pathSegments[0] === "dashboard" &&
    pathSegments[1] === "projects" &&
    pathSegments[3] === "conversations" &&
    pathSegments.length > 4
  );
}

/** True on `/dashboard/projects/{pid}/conversations/{cid}` workbench routes. */
export function useIsWorkbenchRoute(): boolean {
  const pathname = usePathname();
  return parseWorkbenchRoute(pathname);
}

/**
 * Shared hook to get navigation data (current project, conversation, view type)
 * Eliminates duplication between breadcrumb and project pages
 */
export function useNavigationData(): NavigationData {
  const pathname = usePathname();
  const { projects, fetchProjects } = useProjectStore();
  const { conversations } = useConversationStore();
  
  // Parse the path to understand current location
  const pathSegments = pathname.split("/").filter(Boolean);

  const isProjectConversationDetailRoute = parseWorkbenchRoute(pathname);
  const conversationIdFromPath = isProjectConversationDetailRoute ? pathSegments[4] ?? null : null;
  const conversationId = conversationIdFromPath;

  // Conversation "view" should mean full chat detail, not conversations list.
  const isConversationView = isProjectConversationDetailRoute;
  const isProjectDetailView =
    pathname.startsWith("/dashboard/projects/") &&
    pathSegments.length > 2;

  // Project ID from path (for project-scoped routes: /dashboard/projects/prj_xxx/...)
  const projectIdFromPath =
    pathSegments[0] === "dashboard" &&
    pathSegments[1] === "projects" &&
    pathSegments.length > 2 &&
    pathSegments[2] !== "conversation" ? pathSegments[2] : null;

  // Section and entity IDs from path for experiments, insights, learnings
  let section: BreadcrumbSection = null;
  let experimentId: string | null = null;
  let learningId: string | null = null;
  let insightId: string | null = null;
  let hypothesisId: string | null = null;
  if (projectIdFromPath && pathSegments.length === 3) {
    section = "overview";
  } else if (projectIdFromPath && pathSegments.length > 3) {
    const segment3 = pathSegments[3];
    if (segment3 === "conversations") {
      section = "conversations";
    } else if (segment3 === "experiments") {
      section = "experiments";
      experimentId = pathSegments[4] ?? null;
    } else if (segment3 === "hypotheses") {
      section = "hypotheses";
      hypothesisId = pathSegments[4] ?? null;
    } else if (segment3 === "insights") {
      section = "insights";
      insightId = pathSegments[4] ?? null;
    } else if (segment3 === "learnings") {
      section = "learnings";
      learningId = pathSegments[4] ?? null;
    }
  }

  // Fetch data when needed
  useEffect(() => {
    if (projects.length === 0) {
      fetchProjects();
    }
  }, [projects.length, fetchProjects]);

  // Get current project and conversation details
  let currentProject: CNSProject | null = null;
  let currentConversation: CNSConversation | null = null;
  let projectId: string | null = projectIdFromPath;

  if (isProjectDetailView && pathSegments.length > 2) {
    projectId = pathSegments[2];
    currentProject = projects.find((p) => p.id === projectId) ?? null;
  }

  // Enhanced conversation detection - check both path and search params
  if ((isConversationView || conversationId) && conversationId) {
    let conv: CNSConversation =
      conversations.find((c) => c.id === conversationId) ??
      ({
        id: conversationId,
        title: "Loading...",
        createdAt: new Date(0).toISOString(),
        projectId: projectId ?? "",
        access: {
          canEditConversation: false,
          canViewProject: false,
          canEditProject: false,
        },
      } satisfies CNSConversation);

    currentConversation = conv;

    if (conv.projectId) {
      currentProject = projects.find((p) => p.id === conv.projectId) ?? null;
      projectId = conv.projectId;
    }
  }

  return {
    isConversationView,
    isProjectDetailView,
    currentProject,
    currentConversation,
    projectId,
    conversationId,
    experimentId,
    learningId,
    insightId,
    hypothesisId,
    section,
    pathSegments,
  };
}
